import React, { useState, useEffect } from "react";
import { Box, Button, TextField, Switch, FormControlLabel, Typography, MenuItem } from "@mui/material";
import { apiRequest, listOrgs, getBearerToken } from "../services/tenantUserAdminService";
const API_BASE_URL = process.env.REACT_APP_ACCOUNTING_API_BASE_URL || "http://localhost:4001/api/v1";

export default function LicenseManagement() {
  const [tenantId, setTenantId] = useState("");
  const [loading, setLoading] = useState(false);
  const [features, setFeatures] = useState({});
  const [error, setError] = useState(null);
  const [orgs, setOrgs] = useState([]);

  async function loadFeatures() {
    setError(null);
    if (!tenantId) return setFeatures({});
    setLoading(true);
    try {
      // Try the shared apiRequest first
      try {
        const data = await apiRequest(`/admin/licenses/tenants/${encodeURIComponent(tenantId)}`, { method: "GET" });
        const got = data.features || {};
        if (got.confirmation_statement_submission === undefined) got.confirmation_statement_submission = false;
        setFeatures(got);
        return;
      } catch (apiErr) {
        // Fall back to direct fetch to surface full URL/status for debugging
        console.warn('apiRequest failed, falling back to direct fetch:', apiErr);
      }

      const token = await getBearerToken();
      const url = `${API_BASE_URL}/admin/licenses/tenants/${encodeURIComponent(tenantId)}`;
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        const msg = payload?.error || `Request failed with status ${resp.status}`;
        const err = new Error(msg);
        err.status = resp.status;
        err.payload = payload;
        err.url = url;
        throw err;
      }
      const got = payload.features || {};
      if (got.confirmation_statement_submission === undefined) got.confirmation_statement_submission = false;
      setFeatures(got);
    } catch (err) {
      setError((err?.payload?.error) || err.message || JSON.stringify(err));
      // show any structured info too
      setFeatures({ confirmation_statement_submission: false });
      console.error('loadFeatures error:', err);
    } finally {
      setLoading(false);
    }
  }

  async function toggleFeature(key, enabled) {
    setLoading(true);
    setError(null);
    try {
      await apiRequest(`/admin/licenses/tenants/${encodeURIComponent(tenantId)}`, {
        method: "PUT",
        body: { featureKey: key, enabled },
      });
      // refresh
      await loadFeatures();
    } catch (err) {
      setError(err?.payload?.error || err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    async function fetchOrgs() {
      try {
        const { orgs } = await listOrgs();
        setOrgs(orgs || []);
      } catch (err) {
        // ignore on load
        console.error('Failed to load orgs', err);
      }
    }
    fetchOrgs();
    // Intentionally run once on mount; listOrgs is stable across renders
  }, []);

  useEffect(() => {
    if (tenantId) loadFeatures();
  }, [tenantId]);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>Tenant License Management</Typography>
      <Box sx={{ display: "flex", gap: 2, mb: 2, alignItems: "center" }}>
        <TextField
          label="Select Tenant"
          select
          value={tenantId}
          onChange={(e) => setTenantId(e.target.value)}
          sx={{ width: 360, '& .MuiInputBase-root': { height: 56 } }}
          helperText="Choose tenant by organisation name"
          FormHelperTextProps={{ sx: { mt: 0.5 } }}
          SelectProps={{
            renderValue: (value) => {
              if (!value) return "-- Select tenant --";
              const sel = orgs.find(o => (o.id || o.orgId || o.tenantId || "") === value);
              const id = sel ? (sel.id || sel.orgId || sel.tenantId || value) : value;
              const name = sel ? (sel.name || sel.displayName || sel.orgName || id) : value;
              return (
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography sx={{ fontWeight: 700 }}>{name}</Typography>
                  <Typography variant="caption" color="text.secondary">{id}</Typography>
                </Box>
              );
            },
            MenuProps: {
              PaperProps: {
                sx: {
                  bgcolor: 'background.paper',
                  color: 'text.primary',
                  minWidth: 360,
                }
              }
            }
          }}
        >
          <MenuItem value="" sx={{ py: 1.2 }}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography variant="body2" color="text.secondary">-- Select tenant --</Typography>
            </Box>
          </MenuItem>
          {orgs.map((o) => {
            const id = o.id || o.orgId || o.tenantId || "";
            const name = o.name || o.displayName || o.orgName || id;
            return (
              <MenuItem key={id} value={id} sx={{ py: 1.5, minHeight: 56, bgcolor: 'background.paper', color: 'text.primary' }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                  <Typography sx={{ fontWeight: 700, color: 'text.primary' }}>{name}</Typography>
                  <Typography variant="caption" color="text.secondary">{id}</Typography>
                </Box>
              </MenuItem>
            );
          })}
        </TextField>
        <Button variant="contained" onClick={loadFeatures} disabled={loading || !tenantId} sx={{ height: 56, alignSelf: 'center', mt: 0 }}>Load</Button>
      </Box>

      {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}

      <Box>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>Licensed features for tenant</Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box>
              <Typography>AI-assisted Confirmation Statement</Typography>
              <Typography variant="caption" color="text.secondary">Enable AI-assisted automated confirmation statement assistance/submission (requires tenant opt-in).</Typography>
            </Box>
            <FormControlLabel
              control={<Switch checked={!!features.confirmation_statement_submission} onChange={(e) => toggleFeature('confirmation_statement_submission', e.target.checked)} />}
              label={features.confirmation_statement_submission ? "Enabled" : "Disabled"}
              labelPlacement="start"
            />
          </Box>

          {/* Placeholder for future features - show any existing keys too */}
          {Object.keys(features).filter(k => k !== 'confirmation_statement_submission').length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2">Other feature flags</Typography>
              {Object.keys(features).filter(k => k !== 'confirmation_statement_submission').map((k) => (
                <Box key={k} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1 }}>
                  <Typography sx={{ mr: 2 }}>{k}</Typography>
                  <FormControlLabel
                    control={(<Switch checked={!!features[k]} onChange={(e) => toggleFeature(k, e.target.checked)} />)}
                    label={features[k] ? "Enabled" : "Disabled"}
                    labelPlacement="start"
                  />
                </Box>
              ))}
            </Box>
          )}

        </Box>
      </Box>
    </Box>
  );
}

